import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL } from '../../constants/api.constants';
// Clerk-backed auth bridge as of 2026-04-20. Replaces ../auth/Auth (Cognito).
// The Cognito Auth.ts module is still on disk for the in-progress migration
// but no longer wired into the request path.
import { getAuthHeaders, refreshToken } from '../auth/clerkAuth';

/**
 * Standard response format for API calls
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  timestamp: string;
}

/**
 * Extended axios request config with additional options
 */
export interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  requiresAuth?: boolean;
}

/**
 * Hard sign-out + redirect to /auth/login. Called from the 401 interceptor
 * when refresh has already been tried and failed, or when refresh itself
 * throws. Idempotent under burst-401 (multiple parallel requests all
 * hitting unauthorized at once) via an in-flight guard so we don't queue
 * 12 sign-outs and bounce the location.href twice mid-redirect.
 *
 * Uses window.Clerk.signOut() directly because the React useClerk() hook
 * isn't available in axios-interceptor scope. ClerkProvider mounts a
 * window.Clerk global on app start — present whenever the user has
 * loaded the app, which is the only context where a 401 can happen.
 */
let signingOut = false;
function forceSignOutAndRedirect(): void {
  if (signingOut) return;
  // Don't redirect in demo mode — demo doesn't use real auth. Route-authoritative
  // (matches useIsDemo): demo is the /demo/* path, not a stale localStorage flag.
  // This is the non-React consumer the hook can't cover (axios-interceptor scope).
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/demo')) return;
  signingOut = true;
  // eslint-disable-next-line no-console
  console.warn('[apiClient] auth refresh failed — signing out and redirecting to /auth/login');
  // Route through ?force-signout=1 so the login view's mount-time
  // sign-out fires even if Clerk's React-side state is still stale and
  // the SignIn component would otherwise auto-redirect us back to
  // afterSignInUrl ("/organizations") on a partial session. Belt-and-
  // suspenders: also signOut() here so cookies are cleared as early
  // as possible.
  const clerk = (typeof window !== 'undefined' ? (window as any).Clerk : null) ?? null;
  const target = '/sign-in';
  const finish = () => {
    if (typeof window === 'undefined') return;
    const onLoginAlready = window.location.pathname === '/sign-in';
    if (!onLoginAlready) {
      window.location.replace(target);
    }
  };
  try {
    if (clerk?.signOut) {
      Promise.resolve(clerk.signOut()).then(finish, finish);
      return;
    }
  } catch {
    // fall through
  }
  finish();
}

/**
 * Creates a configured axios instance with auth interceptors
 */
export const createApiClient = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
    // Explicitly set adapter to 'fetch' for consistent behavior with axios 1.10.0+
    adapter: 'fetch',
  });

  // Add request interceptor to include auth headers
  instance.interceptors.request.use(
    async (config) => {
      // Skip auth for endpoints that don't require it
      /*
      if (config.requiresAuth === false) {
        return config;
      }
      */

      // Get auth headers from Auth module
      const headers = await getAuthHeaders();

      // Add headers to request
      Object.keys(headers).forEach(key => {
        config.headers[key] = headers[key];
      });

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Handle token refresh on 401 errors. If the refresh attempt also 401s
  // (or refresh returns false / throws), the session is genuinely dead —
  // sign the user out cleanly and bounce them to /auth/login. Showing
  // "Unauthorized" with a Retry button on, e.g., /organizations is just
  // dressed-up "your session is gone" without telling them how to fix it.
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest?._retry) {
        originalRequest._retry = true;

        try {
          const refreshed = await refreshToken();

          if (refreshed) {
            const headers = await getAuthHeaders();
            Object.keys(headers).forEach((key) => {
              originalRequest.headers[key] = headers[key];
            });
            return instance(originalRequest);
          }
        } catch (_refreshError) {
          // refresh blew up — fall through to forceSignOutAndRedirect
        }

        // Refresh either returned false or threw — the session is dead.
        forceSignOutAndRedirect();
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// Create a singleton instance
export const apiClient = createApiClient();

// Create a raw axios instance without auth headers (for S3 uploads)
export const rawAxios = axios.create({
  timeout: 30000,
});

/**
 * Helper function to create a standardized API response
 */
export const createApiResponse = <T>(
  success: boolean,
  data?: T,
  message?: string
): ApiResponse<T> => ({
  success,
  data,
  message,
  timestamp: new Date().toISOString(),
});

/**
 * Helper for error handling
 */
export const handleApiError = <T>(error: unknown): ApiResponse<T> => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    console.error('API Error:', axiosError);
    
    if (axiosError.response) {
      // Server responded with an error
      const errorMessage = 
        typeof axiosError.response.data === 'object' && axiosError.response.data
          ? (axiosError.response.data as any).message 
          : 'Server error';
          
      return createApiResponse<T>(false, undefined, errorMessage);
    } else if (axiosError.request) {
      // Request was made but no response
      return createApiResponse<T>(false, undefined, 'No response from server');
    }
  }
  
  // Generic error fallback
  return createApiResponse<T>(
    false, 
    undefined, 
    error instanceof Error ? error.message : 'Unknown error occurred'
  );
};

/**
 * Generic API request function with type safety
 */
export const createApiRequest = <T>(
  method: 'get' | 'post' | 'put' | 'delete' | 'patch',
  endpoint: string,
  config?: ExtendedAxiosRequestConfig
) => {
  return async (data?: any): Promise<T> => {
    try {
      let response: AxiosResponse;
      
      // Execute appropriate request based on method
      switch (method) {
        case 'get':
          response = await apiClient.get(endpoint, { ...config, params: data });
          break;
        case 'delete':
          response = await apiClient.delete(endpoint, { ...config, params: data });
          break;
        case 'post':
          response = await apiClient.post(endpoint, data, config);
          break;
        case 'put':
          response = await apiClient.put(endpoint, data, config);
          break;
        case 'patch':
          response = await apiClient.patch(endpoint, data, config);
          break;
      }

      return response.data as T;
    } catch (error) {
      // Let errors propagate naturally - calling code can handle with try/catch
      throw error;
    }
  };
};

export default apiClient;